import React, { useEffect, useMemo, useState } from "react";
import {
  Send,
  RotateCcw,
  CheckCircle2,
  Loader2,
  DollarSign,
  ExternalLink,
  History,
  Zap,
  Receipt,
  FileText,
  Sparkles,
  Download,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  PAYMENT_STATUSES,
  PRICING_TYPES,
  updateLeadBilling,
  markLeadPaid,
  sendPaymentRequest,
  regenerateInvoice,
  invoiceDownloadUrl,
} from "@/lib/api";

const STRIPE_PAYMENT_LINKS_URL = "https://dashboard.stripe.com/payment-links/create";

export const PAYMENT_COLOR = {
  unpaid: { bg: "rgba(220,38,38,0.08)", color: "#991B1B", border: "rgba(220,38,38,0.25)" },
  needs_pricing: { bg: "rgba(234,88,12,0.12)", color: "#9A3412", border: "rgba(234,88,12,0.35)" },
  custom_payment_created: { bg: "rgba(139,92,246,0.12)", color: "#5B21B6", border: "rgba(139,92,246,0.32)" },
  payment_link_sent: { bg: "rgba(212,175,55,0.16)", color: "#7A5E12", border: "rgba(212,175,55,0.4)" },
  paid: { bg: "rgba(16,185,129,0.14)", color: "#065F46", border: "rgba(16,185,129,0.4)" },
  partial_payment: { bg: "rgba(59,130,246,0.12)", color: "#1E3A8A", border: "rgba(59,130,246,0.35)" },
  remaining_balance: { bg: "rgba(234,88,12,0.12)", color: "#9A3412", border: "rgba(234,88,12,0.35)" },
  confirmed_scheduled: { bg: "rgba(16,185,129,0.12)", color: "#065F46", border: "rgba(16,185,129,0.35)" },
  cancelled: { bg: "rgba(100,116,139,0.14)", color: "#334155", border: "rgba(100,116,139,0.3)" },
};

export const PRICING_COLOR = {
  Standard: { bg: "rgba(11,37,69,0.06)", color: "#0B2545", border: "rgba(11,37,69,0.18)" },
  Rush: { bg: "rgba(212,175,55,0.16)", color: "#7A5E12", border: "rgba(212,175,55,0.35)" },
  "Custom Payment": { bg: "rgba(139,92,246,0.12)", color: "#5B21B6", border: "rgba(139,92,246,0.32)" },
  "Facility / Provider": { bg: "rgba(20,184,166,0.12)", color: "#115E59", border: "rgba(20,184,166,0.32)" },
};

export const paymentLabel = (value) =>
  PAYMENT_STATUSES.find((p) => p.value === value)?.label || value;

export const PaymentPill = ({ value }) => {
  const c = PAYMENT_COLOR[value] || PAYMENT_COLOR.unpaid;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold border"
      style={{ background: c.bg, color: c.color, borderColor: c.border }}
      data-testid={`payment-pill-${value}`}
    >
      {paymentLabel(value)}
    </span>
  );
};

export const PricingPill = ({ value }) => {
  const v = value || "Standard";
  const c = PRICING_COLOR[v] || PRICING_COLOR.Standard;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold border"
      style={{ background: c.bg, color: c.color, borderColor: c.border }}
      data-testid={`pricing-pill-${v.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {v}
    </span>
  );
};

const fmt = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

// -----------------------------------------------------------------------------
// SendPaymentButton — reusable one-click payment-request trigger.
// Used both inside the Billing panel and in the parent dialog's sticky footer.
// -----------------------------------------------------------------------------
export const SendPaymentButton = ({ lead, onChange, size = "lg", className = "" }) => {
  const [busy, setBusy] = useState(false);
  const due = parseFloat(lead?.amount_due);
  const hasAmount = Number.isFinite(due) && due > 0;
  const hasEmail = !!(lead?.email || "").trim();
  const disabled = busy || !hasAmount || !hasEmail;
  const alreadySent = !!lead?.payment_request_sent_at;

  const onClick = async () => {
    if (!hasAmount) {
      toast.error("Set an Amount Due > 0 first.");
      return;
    }
    if (!hasEmail) {
      toast.error("This lead has no email — cannot send a payment request.");
      return;
    }
    setBusy(true);
    try {
      const res = await sendPaymentRequest(lead.id);
      onChange?.(res.lead);
      if (res.customer_email_id) {
        toast.success("Payment request sent — Stripe link delivered to customer.");
      } else if (res.customer_email_error) {
        toast.warning(`Stripe link generated, but customer email failed: ${res.customer_email_error}`);
      } else {
        toast.success("Stripe checkout generated.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send payment request.");
    } finally {
      setBusy(false);
    }
  };

  const isLg = size === "lg";
  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${isLg ? "h-12 text-base" : "h-9 text-xs"} font-semibold tracking-wide ${className}`}
      style={{ background: "#0B2545", color: "#FFFFFF" }}
      data-testid={isLg ? "action-send-payment-request" : "footer-send-payment-request"}
    >
      {busy
        ? <Loader2 className={`${isLg ? "h-4 w-4" : "h-3.5 w-3.5"} animate-spin mr-2`} />
        : (alreadySent
            ? <RotateCcw className={`${isLg ? "h-4 w-4" : "h-3.5 w-3.5"} mr-2`} />
            : <Zap className={`${isLg ? "h-4 w-4" : "h-3.5 w-3.5"} mr-2`} />)}
      {alreadySent ? "Resend Payment Request" : "Send Payment Request"}
      {isLg ? (
        <span className="hidden sm:inline ml-2 text-[10px] uppercase tracking-wider opacity-80">
          One-click · Stripe + email + log
        </span>
      ) : null}
    </Button>
  );
};

export const BillingPanel = ({ lead, onChange }) => {
  const [amountDue, setAmountDue] = useState(
    lead.amount_due != null ? String(lead.amount_due) : "",
  );
  const [amountPaid, setAmountPaid] = useState(
    lead.amount_paid != null ? String(lead.amount_paid) : "0",
  );
  const [customLink, setCustomLink] = useState(lead.custom_payment_link || "");
  const [savingAmounts, setSavingAmounts] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [sendingAction, setSendingAction] = useState(null);
  const [regeneratingInvoice, setRegeneratingInvoice] = useState(false);
  const [justRefreshedLink, setJustRefreshedLink] = useState(false);

  useEffect(() => {
    setAmountDue(lead.amount_due != null ? String(lead.amount_due) : "");
    setAmountPaid(lead.amount_paid != null ? String(lead.amount_paid) : "0");
    setCustomLink(lead.custom_payment_link || "");
  }, [lead.id, lead.amount_due, lead.amount_paid, lead.custom_payment_link]);

  const remaining = useMemo(() => {
    const d = parseFloat(amountDue || "0") || 0;
    const p = parseFloat(amountPaid || "0") || 0;
    return Math.max(0, d - p);
  }, [amountDue, amountPaid]);

  const parsedDue = useMemo(() => {
    const v = parseFloat(amountDue);
    return Number.isFinite(v) ? v : null;
  }, [amountDue]);

  const saveAmounts = async () => {
    try {
      setSavingAmounts(true);
      const body = {};
      const d = parseFloat(amountDue);
      const p = parseFloat(amountPaid);
      if (!Number.isNaN(d)) body.amount_due = d;
      if (!Number.isNaN(p)) body.amount_paid = p;
      if (!Object.keys(body).length) {
        toast.error("Nothing to save.");
        return;
      }
      const prevSessionId = lead.stripe_session_id;
      const prevLink = lead.custom_payment_link;
      const updated = await updateLeadBilling(lead.id, body);
      onChange?.(updated);
      // Backend auto-creates/refreshes the Stripe link when amount_due changes
      // on an unpaid lead. Detect that and surface a clear confirmation.
      const linkChanged =
        updated?.custom_payment_link &&
        (updated.custom_payment_link !== prevLink || updated.stripe_session_id !== prevSessionId);
      if (linkChanged) {
        setJustRefreshedLink(true);
        toast.success("Amount saved · Stripe payment link generated", {
          description: `New checkout link for $${Number(updated.amount_due).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`,
        });
        // Highlight the new link briefly
        setTimeout(() => setJustRefreshedLink(false), 6000);
      } else {
        toast.success("Billing updated.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update billing.");
    } finally {
      setSavingAmounts(false);
    }
  };

  const handleRegenerateInvoice = async () => {
    try {
      setRegeneratingInvoice(true);
      const res = await regenerateInvoice(lead.id);
      onChange?.(res.lead);
      toast.success(`Invoice ${res.invoice_number} regenerated and emailed.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to regenerate invoice.");
    } finally {
      setRegeneratingInvoice(false);
    }
  };

  const setStatus = async (status) => {
    try {
      const updated = await updateLeadBilling(lead.id, { payment_status: status });
      onChange?.(updated);
      toast.success(`Payment status: ${paymentLabel(status)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status.");
    }
  };

  const setPricingType = async (pricing_type) => {
    try {
      const updated = await updateLeadBilling(lead.id, { pricing_type });
      onChange?.(updated);
      toast.success(`Pricing type: ${pricing_type}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update pricing type.");
    }
  };

  const createStripeInvoice = async () => {
    if (navigator.clipboard?.writeText) {
      try { await navigator.clipboard.writeText(lead.email); } catch { /* ignore */ }
    }
    window.open(STRIPE_PAYMENT_LINKS_URL, "_blank", "noopener,noreferrer");
  };

  const markPaid = async () => {
    try {
      setSendingAction("mark_paid");
      const updated = await markLeadPaid(lead.id);
      onChange?.(updated);
      toast.success("Marked as paid.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark paid.");
    } finally {
      setSendingAction(null);
    }
  };

  const isBusy = (action) => sendingAction === action;

  const saveCustomLink = async () => {
    const link = customLink.trim();
    if (link && !/^https?:\/\//i.test(link)) {
      toast.error("Link must start with http:// or https://");
      return;
    }
    try {
      setSavingLink(true);
      const updated = await updateLeadBilling(lead.id, { custom_payment_link: link });
      onChange?.(updated);
      toast.success(link ? "Payment link saved." : "Payment link cleared.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save payment link.");
    } finally {
      setSavingLink(false);
    }
  };

  return (
    <div
      className="rounded-xl border p-5"
      style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
      data-testid="billing-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4" style={{ color: "#D4AF37" }} />
          <span
            className="text-[11px] uppercase tracking-[0.22em] font-medium"
            style={{ color: "#475569" }}
          >
            Billing
          </span>
        </div>
        <div className="flex items-center gap-2">
          <PricingPill value={lead.pricing_type || "Standard"} />
          <PaymentPill value={lead.payment_status || "unpaid"} />
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#475569" }}>
            Pricing type
          </Label>
          <Select value={lead.pricing_type || "Standard"} onValueChange={setPricingType}>
            <SelectTrigger className="h-10 mt-1" data-testid="billing-pricing-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRICING_TYPES.map((p) => (
                <SelectItem
                  key={p.value}
                  value={p.value}
                  data-testid={`billing-pricing-option-${p.value.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#475569" }}>
            Payment status
          </Label>
          <Select value={lead.payment_status || "unpaid"} onValueChange={setStatus}>
            <SelectTrigger className="h-10 mt-1" data-testid="billing-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_STATUSES.map((p) => (
                <SelectItem key={p.value} value={p.value} data-testid={`billing-status-option-${p.value}`}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#475569" }}>
            Amount Due
          </Label>
          <div className="relative mt-1">
            <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
            <Input
              value={amountDue}
              onChange={(e) => setAmountDue(e.target.value)}
              inputMode="decimal"
              placeholder="0.00"
              className="h-10 pl-8"
              data-testid="billing-amount-due"
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#475569" }}>
            Amount Paid
          </Label>
          <div className="relative mt-1">
            <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
            <Input
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
              inputMode="decimal"
              placeholder="0.00"
              className="h-10 pl-8"
              data-testid="billing-amount-paid"
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#475569" }}>
            Remaining
          </Label>
          <div
            className="mt-1 h-10 flex items-center px-3 rounded-md border"
            style={{ borderColor: "#E2E8F0", background: "#FFFFFF" }}
            data-testid="billing-remaining"
          >
            <span className="text-sm font-semibold" style={{ color: "#0B2545" }}>
              {fmt(remaining)}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          onClick={saveAmounts}
          disabled={savingAmounts}
          className="h-9 font-semibold"
          style={{ background: "#0B2545", color: "#FFFFFF" }}
          data-testid="billing-save-amounts"
        >
          {savingAmounts ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
          Save Amount
        </Button>
        <p className="text-[10px] leading-snug max-w-[260px]" style={{ color: "#64748B" }}>
          Saving auto-generates a Stripe payment link for the new amount and replaces any older unpaid link.
        </p>
      </div>

      <div className="mt-5">
        <div
          className="text-[10px] uppercase tracking-[0.22em] font-medium mb-2"
          style={{ color: "#475569" }}
        >
          Payment workflow
        </div>

        {/* ONE-CLICK Send Payment Request */}
        <SendPaymentButton lead={lead} onChange={onChange} size="lg" className="w-full" />

        {/* Helper line — what one-click does */}
        <p className="mt-2 text-[11px] leading-relaxed" style={{ color: "#64748B" }}>
          Generates the Stripe Checkout link, emails it to <strong>{lead.email || "the customer"}</strong> with the T&amp;J branded template, alerts the admin inbox, and logs the action to Job History.
          {parsedDue && parsedDue > 0
            ? <> Customer will pay <strong>${parsedDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.</>
            : <> <span style={{ color: "#9A3412" }}>Set Amount Due above first.</span></>}
        </p>

        {/* Saved checkout link (after Send or auto-generated after Save Amount) */}
        {lead.custom_payment_link ? (
          <div
            className={`mt-3 rounded-xl border p-3 transition ${justRefreshedLink ? "ring-2 ring-offset-1" : ""}`}
            style={{
              borderColor: justRefreshedLink ? "#D4AF37" : "#E2E8F0",
              background: justRefreshedLink ? "rgba(212,175,55,0.06)" : "#FFFFFF",
              ...(justRefreshedLink ? { boxShadow: "0 0 0 2px rgba(212,175,55,0.18)" } : {}),
            }}
            data-testid="billing-saved-link-card"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5" style={{ color: justRefreshedLink ? "#D4AF37" : "#10B981" }} />
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: justRefreshedLink ? "#7A5E12" : "#065F46" }}>
                  {justRefreshedLink ? "New link generated" : "Stripe link ready"}
                </span>
              </div>
              {lead.stripe_session_amount != null ? (
                <span className="text-[10px] font-semibold" style={{ color: "#0B2545" }}>
                  {fmt(Number(lead.stripe_session_amount))}
                </span>
              ) : null}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <DollarSign className="h-3.5 w-3.5 shrink-0" style={{ color: "#635BFF" }} />
              <a
                href={lead.custom_payment_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] truncate flex-1 underline"
                style={{ color: "#0B2545" }}
                title={lead.custom_payment_link}
                data-testid="billing-custom-link-saved"
              >
                {lead.custom_payment_link}
              </a>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-[11px]"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(lead.custom_payment_link);
                    toast.success("Link copied.");
                  } catch {
                    toast.message("Select the link to copy.");
                  }
                }}
                data-testid="billing-copy-link"
              >
                Copy
              </Button>
            </div>
            {lead.stripe_session_created_at ? (
              <p className="mt-1.5 text-[10px]" style={{ color: "#94A3B8" }}>
                Created {new Date(lead.stripe_session_created_at).toLocaleString()}
                {lead.payment_request_sent_at
                  ? ` · Sent ${new Date(lead.payment_request_sent_at).toLocaleString()}`
                  : " · Not yet sent to customer"}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Invoice PDF — auto-generated after Stripe webhook confirms payment */}
        {lead.invoice_pdf_file_id ? (
          <div
            className="mt-3 rounded-xl border p-3"
            style={{ borderColor: "rgba(16,185,129,0.4)", background: "rgba(16,185,129,0.06)" }}
            data-testid="billing-invoice-card"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5" style={{ color: "#065F46" }} />
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: "#065F46" }}>
                  Invoice generated
                </span>
              </div>
              {lead.invoice_number ? (
                <span className="text-[10px] font-semibold" style={{ color: "#0B2545" }}>{lead.invoice_number}</span>
              ) : null}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <a
                href={invoiceDownloadUrl(lead.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[11px] font-semibold border"
                style={{ borderColor: "#065F46", color: "#065F46", background: "#FFFFFF" }}
                data-testid="billing-download-invoice"
              >
                <Download className="h-3 w-3" /> Download PDF
              </a>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleRegenerateInvoice}
                disabled={regeneratingInvoice}
                className="h-8 text-[11px]"
                style={{ borderColor: "#0B2545", color: "#0B2545" }}
                data-testid="billing-regenerate-invoice"
              >
                {regeneratingInvoice ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                Regenerate & resend
              </Button>
            </div>
            {lead.invoice_sent_at ? (
              <p className="mt-1.5 text-[10px]" style={{ color: "#94A3B8" }}>
                Emailed to <strong>{lead.email}</strong> on {new Date(lead.invoice_sent_at).toLocaleString()}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Manual override — Mark as Paid (in case of off-Stripe payment) */}
        <div className="mt-3">
          <Button
            type="button"
            onClick={markPaid}
            disabled={isBusy("mark_paid") || lead.payment_status === "paid"}
            variant="outline"
            className="w-full h-9 text-xs"
            style={{ borderColor: "#D4AF37", color: "#7A5E12", background: "rgba(212,175,55,0.08)" }}
            data-testid="action-mark-paid"
          >
            {isBusy("mark_paid") ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
            Manual override · Mark as Paid
          </Button>
          <p className="mt-1.5 text-[10px]" style={{ color: "#94A3B8" }}>
            Only use this when payment was received outside Stripe (cash, check, off-platform). Stripe payments auto-reconcile via webhook.
          </p>
        </div>

        {/* Manual link entry — collapsed, for facilities w/ custom links */}
        {lead.pricing_type === "Custom Payment" ? (
          <details className="mt-4 group">
            <summary
              className="cursor-pointer text-[11px] uppercase tracking-wider font-medium select-none"
              style={{ color: "#64748B" }}
            >
              Advanced · Paste a manual payment link
            </summary>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <Input
                value={customLink}
                onChange={(e) => setCustomLink(e.target.value)}
                placeholder="https://buy.stripe.com/..."
                className="h-9 flex-1 text-xs"
                data-testid="billing-custom-link-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={saveCustomLink}
                disabled={savingLink}
                className="h-9 text-xs"
                style={{ borderColor: "#0B2545", color: "#0B2545" }}
                data-testid="billing-save-custom-link"
              >
                {savingLink ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
                Save link
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={createStripeInvoice}
                className="h-9 text-xs"
                style={{ borderColor: "#635BFF", color: "#635BFF" }}
                data-testid="action-open-stripe-dashboard"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Open Stripe
              </Button>
            </div>
          </details>
        ) : null}

        {/* Activity log / Job history */}
        {Array.isArray(lead.activity_log) && lead.activity_log.length > 0 ? (
          <div
            className="mt-5 rounded-lg border"
            style={{ borderColor: "#E2E8F0", background: "#FFFFFF" }}
            data-testid="billing-activity-log"
          >
            <div
              className="px-3 py-2 border-b flex items-center gap-1.5"
              style={{ borderColor: "#E2E8F0" }}
            >
              <History className="h-3.5 w-3.5" style={{ color: "#7A5E12" }} />
              <span className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#475569" }}>
                Job history
              </span>
            </div>
            <ul className="divide-y" style={{ borderColor: "#E2E8F0" }}>
              {[...lead.activity_log].reverse().slice(0, 6).map((entry, i) => (
                <li key={i} className="px-3 py-2 flex items-start gap-2.5" data-testid={`activity-row-${i}`}>
                  <span
                    className="mt-0.5 inline-flex shrink-0 h-1.5 w-1.5 rounded-full"
                    style={{
                      background:
                        entry.action === "payment_received" ? "#10B981" :
                        entry.action === "payment_request_sent" ? "#0B2545" : "#94A3B8",
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] leading-snug" style={{ color: "#0B2545" }}>
                      {entry.summary}
                    </div>
                    <div className="text-[10px]" style={{ color: "#94A3B8" }}>
                      {new Date(entry.ts).toLocaleString()} · {entry.actor || "system"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <p className="mt-3 text-[11px]" style={{ color: "#94A3B8" }}>
          All emails are sent to <strong>{lead.email || "—"}</strong>.
        </p>
        {lead.last_billing_email_at ? (
          <p className="text-[11px] mt-1" style={{ color: "#94A3B8" }} data-testid="billing-last-sent">
            Last email: <strong>{lead.last_billing_email_action}</strong> ·{" "}
            {new Date(lead.last_billing_email_at).toLocaleString()}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default BillingPanel;
